'use client'

import { ChevronRightIcon } from '@repo/studio-design-system-ui-icons/arrows'
import { Button as DsButton } from '@repo/studio-design-system-ui-button'
import { Settings04Icon, XCloseIcon } from '@repo/studio-design-system-ui-icons/general'
import * as React from 'react'
import { tw } from 'utils/classnames'
import { StudioButton } from '@repo/studio-components-buttons/studio-button'
import * as Accordion from 'primitives/accordion'
import { m, LazyMotion, useAnimationControls, domAnimation } from 'framer-motion'
import * as Dialog from '@radix-ui/react-dialog'
import { IconButton } from 'primitives/buttons/icon-button'

import { NavigationSidebar } from '../navigation-sidebar'
import { Filter } from './filter'
import { SEARCHABLE_FILTERS } from './constants'

const overlayVariants = {
  expanded: { opacity: 1, transition: { duration: 0.15 } },
  collapsed: { opacity: 0, transition: { duration: 0.15 } },
}

const contentVariants = {
  expanded: {
    x: [-50, 0],
    opacity: [0, 1],
    transition: { ease: 'easeOut' as const, duration: 0.2 },
  },
  collapsed: {
    x: [0, -50],
    opacity: [1, 0],
    transition: { ease: 'easeOut' as const, duration: 0.2 },
  },
}

const AccordionIcon = (
  <ChevronRightIcon
    className="h-[16px] w-[16px] text-gray-400 transition-transform group-data-[state='open']/trigger:rotate-90"
    aria-hidden
  />
)

interface FiltersMobileProps {
  isFavorites?: boolean
  favoritesText: string
  allTemplatesText: string
  subcategoriesText: string
  filtersText: string
}
export function FilterMobile({
  isFavorites = false,
  favoritesText,
  allTemplatesText,
  subcategoriesText,
  filtersText,
}: FiltersMobileProps) {
  const {
    open,
    overlayControls,
    contentControls,
    handleOverlayStateChange,
    onCloseFromContentOverlay,
  } = useTemplateFiltersMobile()

  return (
    <LazyMotion features={domAnimation}>
      <Dialog.Root key="hamburger-menu" open={open} onOpenChange={handleOverlayStateChange}>
        <Dialog.Trigger asChild>
          <DsButton
            className="h-9 border border-gray-800 bg-transparent px-3 text-gray-100 hover:bg-gray-700 hover:text-gray-100 focus:bg-gray-700 focus:text-gray-100 active:bg-gray-700 active:text-gray-100 sm:px-4 lg:hidden"
            size="sm"
            variant="secondary"
          >
            <Settings04Icon className="mr-2 size-4 text-gray-100" />{' '}
            <span className="text-12 whitespace-nowrap font-medium">{filtersText}</span>
          </DsButton>
        </Dialog.Trigger>

        <Dialog.Portal>
          <m.div
            animate={overlayControls}
            variants={overlayVariants}
            key="overlay"
            className="relative"
          >
            <Dialog.Overlay
              forceMount
              className="fixed inset-0 z-30 bg-[#13131552] bg-opacity-80 shadow-[0_10px_10px_0_rgba(0,0,0,0.04)] backdrop-blur-sm"
            />
          </m.div>

          <Dialog.Content key="content" asChild forceMount onClick={onCloseFromContentOverlay}>
            <m.div
              animate={contentControls}
              variants={contentVariants}
              className={tw(
                `rounded-4 bg-gray-800-white fixed left-2 top-2 z-40 flex h-[calc(100dvh-16px)] w-[300px] outline-none`,
              )}
            >
              <div className="flex h-full w-full flex-1 flex-col items-center self-start p-4">
                <div className="mb-4 flex h-[29px] w-full items-center justify-end">
                  <XCloseIcon
                    className="h-4 w-4 text-gray-400"
                    onClick={() => handleOverlayStateChange(false)}
                  />
                </div>
                <Accordion.Root
                  type="single"
                  collapsible
                  className="flex min-h-0 w-full flex-1 flex-col"
                  defaultValue={allTemplatesText}
                >
                  <Accordion.Item value={filtersText}>
                    <Accordion.Header>
                      <Accordion.Trigger
                        className="mb-1 w-full justify-between font-medium text-gray-400 hover:text-gray-50 focus:text-gray-50"
                        icon={AccordionIcon}
                      >
                        <span className="text-gray-400">{filtersText}</span>
                      </Accordion.Trigger>
                    </Accordion.Header>
                    <Accordion.Content className="-mx-2">
                      <div className="flex flex-col gap-2 p-2">
                        {Object.values(SEARCHABLE_FILTERS).map(filter => (
                          <div key={filter.label} className="shrink-0">
                            <Filter filter={filter} />
                          </div>
                        ))}
                      </div>
                    </Accordion.Content>
                  </Accordion.Item>
                  <Accordion.Item value={allTemplatesText} className="flex min-h-0 flex-1 flex-col">
                    <Accordion.Header>
                      <Accordion.Trigger
                        className="mb-1 w-full justify-between font-medium text-gray-400 hover:text-gray-50 focus:text-gray-50"
                        icon={AccordionIcon}
                      >
                        <span className="text-gray-400">{allTemplatesText}</span>
                      </Accordion.Trigger>
                    </Accordion.Header>
                    <Accordion.Content className="-mx-2 !h-full">
                      <div className="h-full overflow-auto">
                        <NavigationSidebar
                          isFavorites={isFavorites}
                          favoritesText={favoritesText}
                          allTemplatesText={allTemplatesText}
                          subcategoriesText={subcategoriesText}
                        />
                      </div>
                    </Accordion.Content>
                  </Accordion.Item>
                </Accordion.Root>
              </div>
            </m.div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </LazyMotion>
  )
}

type StyleCSS = React.CSSProperties & Record<string, string>
interface FiltersMobileStudioProps {
  filtersText?: string
}
export function FilterMobileStudio({ filtersText }: FiltersMobileStudioProps) {
  const {
    open,
    overlayControls,
    contentControls,
    handleOverlayStateChange,
    onCloseFromContentOverlay,
  } = useTemplateFiltersMobile()

  return (
    <LazyMotion features={domAnimation}>
      <Dialog.Root key="hamburger-menu" open={open} onOpenChange={handleOverlayStateChange}>
        <Dialog.Trigger asChild>
          <DsButton
            className="h-9 border border-gray-800 bg-transparent px-3 text-gray-100 hover:bg-gray-700 hover:text-gray-100 focus:bg-gray-700 focus:text-gray-100 active:bg-gray-700 active:text-gray-100 sm:px-4 lg:hidden"
            size="sm"
            variant="secondary"
          >
            <Settings04Icon className="mr-2 size-4 text-gray-100" />
            <span className="text-12 whitespace-nowrap font-medium">
              {filtersText ?? 'All filters'}
            </span>
          </DsButton>
        </Dialog.Trigger>
        <Dialog.Portal>
          <m.div
            animate={overlayControls}
            variants={overlayVariants}
            key="overlay"
            className="relative"
          >
            <Dialog.Overlay
              forceMount
              className="fixed inset-0 z-30 bg-[#13131552] bg-opacity-80 shadow-[0_10px_10px_0_rgba(0,0,0,0.04)] backdrop-blur-sm"
            />
          </m.div>

          <Dialog.Content key="content" asChild forceMount onClick={onCloseFromContentOverlay}>
            <m.div
              style={{ '--max-width': '314px', '--popover-offset': '24px' } as StyleCSS}
              animate={contentControls}
              variants={contentVariants}
              className="rounded-4 fixed left-0 top-0 z-40 flex h-[calc(100dvh-16px)] w-[var(--max-width)] bg-gray-900 outline-none"
            >
              <div className="font-inter relative mt-5 flex w-full flex-col items-start gap-2 bg-gray-900 p-4">
                <div className="flex w-full justify-between">
                  <span className="h-8 text-[18px] font-[700]">Filters</span>
                  <Dialog.Close>
                    <IconButton
                      variant="transparent"
                      className="flex size-8 items-center justify-center"
                    >
                      <XCloseIcon className="size-5 text-gray-400" />
                    </IconButton>
                  </Dialog.Close>
                </div>
                {Object.values(SEARCHABLE_FILTERS).map(filter => (
                  <div key={filter.label} className="relative flex w-full min-w-full shrink-0">
                    <Filter
                      filter={filter}
                      filterContentClassName="max-w-[calc(100vw-var(--popover-offset))]"
                      shouldIgnorePortal
                    />
                  </div>
                ))}
              </div>
            </m.div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </LazyMotion>
  )
}

function useTemplateFiltersMobile() {
  const [open, setOpen] = React.useState(false)

  const overlayControls = useAnimationControls()
  const contentControls = useAnimationControls()

  React.useEffect(() => {
    const animate = async () => {
      if (!open) {
        return
      }

      await overlayControls.start('expanded')
      await contentControls.start('expanded')
    }

    animate()
  }, [open, overlayControls, contentControls])

  function onCloseFromContentOverlay(event: React.MouseEvent<HTMLDivElement>) {
    if (event.target !== event.currentTarget) {
      return
    }

    handleOverlayStateChange(false)
  }

  const handleOverlayStateChange = async (isOpened: boolean) => {
    if (!isOpened) {
      // Ensure content animation starts first and completes before overlay starts
      await contentControls.start('collapsed')
      await overlayControls.start('collapsed')
    }

    setOpen(isOpened)
  }

  return {
    open,
    overlayControls,
    contentControls,
    handleOverlayStateChange,
    onCloseFromContentOverlay,
  }
}
